import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  AdminReviewApiError,
  clearStoredAdminReviewToken,
  connectAdminReviewSession,
  getAdminReviewMandatoryDisclosure,
  getStoredAdminReviewToken,
  type AdminReviewDisclosureItem,
  type AdminReviewDisclosureSection,
  type AdminReviewMandatoryDisclosureResponse,
  type AdminReviewSiteKey,
} from './api/adminReviewApi';
import './pages-review.css';
import './mandatory-disclosure-review.css';

type MandatoryDisclosureReviewProps = {
  siteKey: AdminReviewSiteKey;
};

type StructuredField = {
  key: string;
  label: string;
  value: string;
};

const structuredFieldOrder = ['registered', 'passed', 'passPercentage', 'remarks'];
const structuredFieldLabels: Record<string, string> = {
  registered: 'Registered',
  passed: 'Passed',
  passPercentage: 'Pass %',
  remarks: 'Remarks',
};

function getErrorMessage(error: unknown) {
  if (error instanceof AdminReviewApiError) return error.message;
  return 'The staging Mandatory Disclosure review request failed unexpectedly.';
}

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function humanizeFieldKey(key: string) {
  if (structuredFieldLabels[key]) return structuredFieldLabels[key];
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/^./, (character) => character.toUpperCase());
}

function parseStructuredFields(fieldsJson: string): StructuredField[] {
  try {
    const parsed = JSON.parse(fieldsJson) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];

    return Object.entries(parsed)
      .map(([key, value]) => ({
        key,
        label: humanizeFieldKey(key),
        value: typeof value === 'string' ? value : JSON.stringify(value),
      }))
      .sort((left, right) => {
        const leftIndex = structuredFieldOrder.indexOf(left.key);
        const rightIndex = structuredFieldOrder.indexOf(right.key);
        const normalizedLeft = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
        const normalizedRight = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
        return normalizedLeft - normalizedRight;
      });
  } catch {
    return [];
  }
}

function getSectionEyebrow(section: AdminReviewDisclosureSection) {
  const letterMatch = section.title.match(/^([A-Z]):/);
  if (letterMatch) return `Section ${letterMatch[1]}`;
  if (section.title.startsWith('RESULT:')) return 'Board examination results';
  return 'Mandatory Disclosure section';
}

function assetStateLabel(state: AdminReviewDisclosureItem['assetState']) {
  switch (state) {
    case 'ready': return 'Private asset ready';
    case 'not-linked': return 'No document';
    case 'missing-document': return 'Document missing';
    case 'missing-media': return 'Media missing';
    case 'drift': return 'Metadata drift';
  }
}

function ItemCard({ item }: { item: AdminReviewDisclosureItem }) {
  const structuredFields = useMemo(() => parseStructuredFields(item.fieldsJson), [item.fieldsJson]);
  const showAssetState = item.assetState !== 'not-linked';
  const assetTone = item.assetState === 'ready' ? 'safe' : 'warning';

  return (
    <article className="disclosure-item-card">
      <div className="disclosure-item-heading">
        <div>
          <span className="disclosure-item-key">{item.key}</span>
          <h4>{item.label}</h4>
        </div>
        {showAssetState && (
          <span className={`asset-state ${assetTone}`}>{assetStateLabel(item.assetState)}</span>
        )}
      </div>

      {item.valueText && (
        <div className="disclosure-value">
          <span>Value</span>
          <p>{item.valueText}</p>
        </div>
      )}

      {structuredFields.length > 0 && (
        <div className="disclosure-fields">
          <span>Structured fields</span>
          <div className="disclosure-fields-table-wrap">
            <table className="disclosure-fields-table">
              <thead>
                <tr>
                  {structuredFields.map((field) => <th scope="col" key={field.key}>{field.label}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {structuredFields.map((field) => <td key={field.key}>{field.value}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {item.externalUrl && (
        <div className="disclosure-value">
          <span>External source</span>
          <a href={item.externalUrl} target="_blank" rel="noreferrer noopener">{item.externalUrl}</a>
        </div>
      )}

      {item.document && (
        <div className="disclosure-document">
          <div className="disclosure-document-heading">
            <div>
              <span>Linked document</span>
              <strong>{item.document.title}</strong>
            </div>
            <span className={`status status-${item.document.status.toLowerCase()}`}>{item.document.status}</span>
          </div>

          <dl className="disclosure-document-grid">
            <div><dt>Public flag</dt><dd>{item.document.isPublic ? 'Public' : 'Private'}</dd></div>
            <div><dt>Legacy source</dt><dd>{item.document.legacySourceUrl || '—'}</dd></div>
            <div><dt>Filename</dt><dd>{item.document.media?.originalFilename || '—'}</dd></div>
            <div><dt>Size</dt><dd>{item.document.media ? formatBytes(item.document.media.byteSize) : '—'}</dd></div>
            <div className="wide"><dt>R2 object key</dt><dd>{item.document.media?.storageKey || '—'}</dd></div>
            <div className="wide"><dt>SHA-256</dt><dd className="hash-value">{item.document.media?.sha256 || '—'}</dd></div>
          </dl>
        </div>
      )}
    </article>
  );
}

export default function MandatoryDisclosureReview({ siteKey }: MandatoryDisclosureReviewProps) {
  const [sessionToken, setSessionToken] = useState(() => getStoredAdminReviewToken());
  const [accessKey, setAccessKey] = useState('');
  const [data, setData] = useState<AdminReviewMandatoryDisclosureResponse | null>(null);
  const [selectedSectionKey, setSelectedSectionKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setData(null);
    setSelectedSectionKey('');
    setMessage('');

    if (!sessionToken) return undefined;

    const controller = new AbortController();
    setLoading(true);

    void getAdminReviewMandatoryDisclosure(siteKey, sessionToken, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setData(response);
        setSelectedSectionKey(response.sections[0]?.key ?? '');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof AdminReviewApiError && error.code === 'unauthorized') {
          clearStoredAdminReviewToken();
          setSessionToken('');
        }
        setMessage(getErrorMessage(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [sessionToken, siteKey]);

  const selectedSection = useMemo<AdminReviewDisclosureSection | null>(() => {
    if (!data) return null;
    return data.sections.find((section) => section.key === selectedSectionKey) ?? data.sections[0] ?? null;
  }, [data, selectedSectionKey]);

  const connect = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setConnecting(true);
    setMessage('');
    try {
      const token = await connectAdminReviewSession(accessKey);
      setAccessKey('');
      setSessionToken(token);
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = () => {
    clearStoredAdminReviewToken();
    setSessionToken('');
    setAccessKey('');
    setData(null);
    setSelectedSectionKey('');
    setMessage('');
  };

  if (!sessionToken) {
    return (
      <section className="review-access-card" aria-labelledby="disclosure-review-access-title">
        <div className="review-access-copy">
          <span className="eyebrow">Protected staging access</span>
          <h2 id="disclosure-review-access-title">Review Mandatory Disclosure</h2>
          <p>
            Enter the temporary staging review key to inspect the structured disclosure draft. The key remains only in this browser tab session and no document is made public by opening this review.
          </p>
        </div>
        <form className="review-access-form" onSubmit={connect}>
          <label htmlFor="disclosure-review-access-key">Staging review access key</label>
          <input
            id="disclosure-review-access-key"
            type="password"
            value={accessKey}
            onChange={(event) => setAccessKey(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder="Enter access key"
          />
          <button className="primary-action" type="submit" disabled={connecting || !accessKey.trim()}>
            {connecting ? 'Connecting…' : 'Open read-only review'}
          </button>
          {message && <p className="review-message error" role="alert">{message}</p>}
        </form>
      </section>
    );
  }

  return (
    <div className="pages-review-shell disclosure-review-shell">
      <section className="review-toolbar">
        <div>
          <div className="review-badges">
            <span className="review-badge">Read-only</span>
            <span className="review-badge secondary">Staging</span>
            <span className="review-badge secondary">No publication</span>
          </div>
          <h2>{data?.site.name || 'Mandatory Disclosure review'}</h2>
          <p>
            {data
              ? `${data.site.primaryDomain} · ${data.sectionCount} sections · ${data.itemCount} items · ${data.linkedDocumentCount} linked PDFs`
              : 'Loading structured disclosure records…'}
          </p>
        </div>
        <button className="secondary-action" type="button" onClick={disconnect}>
          Disconnect review session
        </button>
      </section>

      {message && <p className="review-message error" role="alert">{message}</p>}
      {loading && <p className="review-empty">Loading protected staging Mandatory Disclosure records…</p>}

      {!loading && data && data.sections.length === 0 && !message && (
        <section className="review-list-panel disclosure-empty-state">
          <span className="eyebrow">Controlled migration required</span>
          <h3>No structured disclosure records exist in staging yet.</h3>
          <p>Run the reviewed Mandatory Disclosure staging import before editorial review. Nothing has been published.</p>
        </section>
      )}

      {!loading && data && data.sections.length > 0 && (
        <div className="disclosure-review-grid">
          <aside className="review-list-panel disclosure-section-list" aria-label="Mandatory Disclosure sections">
            <div className="review-panel-heading">
              <div>
                <span className="eyebrow">Structured content</span>
                <h3>Sections</h3>
              </div>
              <span>{data.sectionCount}</span>
            </div>

            {data.sections.map((section) => (
              <button
                type="button"
                key={section.id}
                className={selectedSection?.id === section.id ? 'disclosure-section-row active' : 'disclosure-section-row'}
                onClick={() => setSelectedSectionKey(section.key)}
              >
                <span>
                  <strong>{section.title}</strong>
                  <small>{section.itemCount} item{section.itemCount === 1 ? '' : 's'} · {section.key}</small>
                </span>
                <span className={`status status-${section.status.toLowerCase()}`}>{section.status}</span>
              </button>
            ))}
          </aside>

          <section className="review-detail-panel disclosure-detail-panel" aria-label="Mandatory Disclosure section details">
            {selectedSection && (
              <div className="review-detail-content">
                <div className="review-detail-heading">
                  <div>
                    <span className="eyebrow">{getSectionEyebrow(selectedSection)}</span>
                    <h3>{selectedSection.title}</h3>
                    <p>{selectedSection.key} · {selectedSection.itemCount} item{selectedSection.itemCount === 1 ? '' : 's'}</p>
                  </div>
                  <span className={`status status-${selectedSection.status.toLowerCase()}`}>{selectedSection.status}</span>
                </div>

                <div className="disclosure-item-list">
                  {selectedSection.items.map((item) => <ItemCard item={item} key={item.id} />)}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
